import { useId } from "react";

import { closestCenter, DndContext, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faPlus, faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import { times } from "lodash-es";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { ReorderHandle } from "src/base-components/ReorderHandle";
import { TypeOption } from "src/base-components/SchemaTypeSelector";
import { Select } from "src/base-components/Select";
import {
  EnumColor,
  PropertyDefinitionOutputTypeEnum,
} from "src/clients/features-control/api";
import { ENTITY_ID_FIELD_NAME } from "src/datasets/DatasetTable/utils";
import { ErrorHint } from "src/design-system/ErrorHint";
import { IconButton } from "src/design-system/Icon";
import { Input } from "src/design-system/Input";
import { Skeleton } from "src/design-system/Skeleton";
import { getOrderedPropertiesByKey } from "src/entities/entityView/utils";
import {
  EntitySchemaProperty,
  EntitySchemaResource,
} from "src/entities/queries";
import {
  AdvancedOptions,
  MaskingOptions,
} from "src/entities/SchemaEditor/MaskingOptions";
import { PredefinedValuesEditor } from "src/entities/SchemaEditor/PredefinedValuesEditor";
import {
  createNewPropertyKey,
  getNewEntityProperty,
  isPredefinedValuesProperty,
  NEW_PROPERTY_TYPE_OPTIONS,
  validateNewPropertyName,
} from "src/entities/SchemaEditor/utils";
import { isSensitiveType } from "src/entitiesEventsCommon/sensitiveTypes";
import { PropertyTypeSelector } from "src/eventsCatalogue/SchemaEditor/PropertyTypeSelector";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useIsFWDOrAdmin } from "src/store/AuthStore";
import { unslugify } from "src/utils/pythonUtils";

const NEW_PROPERTY_TYPE_SELECT_OPTIONS = NEW_PROPERTY_TYPE_OPTIONS.map(
  (type) => ({
    key: type,
    value: <TypeOption label={type} />,
  }),
);

type PropertyFieldsProps =
  | {
      propertyName: string;
      property: EntitySchemaProperty;
      schemaId: string;
      onRemove: (propertyName: string) => void;
      canReorder: boolean;
      canManageValues: boolean;
      isLoading?: false;
    }
  | {
      isLoading: true;
    };

const PropertyFields: React.FC<PropertyFieldsProps> = (props) => {
  const id = useId();
  const canReorder = "canReorder" in props ? props.canReorder : false;
  const { listeners, setNodeRef, transform, transition } = useSortable({
    id: "propertyName" in props ? props.propertyName : id,
    disabled: props.isLoading || !canReorder,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };
  const { register, formState, getValues, setValue, getFieldState } =
    useFormContext<EntitySchemaResource>();

  if (props.isLoading) {
    return (
      <div className="flex gap-x-2">
        {times(3, (i) => (
          <div key={i} className="w-1/3 flex-1 shrink-0">
            <Skeleton height="h-8" rounded="rounded-lg" width="w-full" />
          </div>
        ))}
      </div>
    );
  }

  const { property, schemaId, propertyName, onRemove, canManageValues } = props;
  if (!property || !propertyName) return null;

  const isNew = !!property._isNew;
  const isPredefinedValuesProp = isPredefinedValuesProperty(property);

  const showDataObfuscation = isFeatureFlagEnabled(
    FEATURE_FLAGS.dataObfuscation,
  );

  const isValidSensitiveType = isSensitiveType(property._type);

  const handleNewTypeChange = (newType: PropertyDefinitionOutputTypeEnum) => {
    const isPredefined =
      newType === PropertyDefinitionOutputTypeEnum.ENUM ||
      newType === PropertyDefinitionOutputTypeEnum.TAGS;
    const currentValues = getValues(`properties.${propertyName}._values`);

    if (isPredefined) {
      if (!currentValues || currentValues.length === 0) {
        setValue(
          `properties.${propertyName}._values`,
          [{ _internal_name: "", _color: EnumColor.GRAY }],
          { shouldDirty: true },
        );
      }
    } else if (currentValues) {
      setValue(`properties.${propertyName}._values`, undefined, {
        shouldDirty: true,
      });
    }
  };

  const nameError = formState.errors.properties?.[propertyName]?._name;

  return (
    <>
      <div
        ref={setNodeRef}
        className="flex flex-col gap-y-3 bg-white"
        style={style}
      >
        <div className="relative z-10 flex gap-x-2 pl-7">
          {canReorder && (
            <div className="absolute left-0 flex h-8 w-7 cursor-grab items-center justify-start">
              <ReorderHandle
                color="text-gray-400"
                listeners={listeners}
                size="xs"
              />
            </div>
          )}
          <div className="w-1/3 flex-1 shrink-0">
            {isNew ? (
              <>
                <Input
                  autoFocus
                  dataLoc={`entity-schema-new-field-name-${propertyName}`}
                  error={!!nameError}
                  placeholder="field_name"
                  variant="monospaced"
                  {...register(`properties.${propertyName}._name`, {
                    required: "This field is required",
                    validate: (value) =>
                      validateNewPropertyName(
                        value ?? "",
                        getValues("properties"),
                        propertyName,
                      ),
                    onChange: (e) => {
                      const { isTouched } = getFieldState(
                        `properties.${propertyName}._display_name`,
                      );
                      if (!isTouched) {
                        setValue(
                          `properties.${propertyName}._display_name`,
                          unslugify(e.target.value),
                          { shouldTouch: false },
                        );
                      }
                    },
                  })}
                />
                {nameError && <ErrorHint>{nameError.message}</ErrorHint>}
              </>
            ) : (
              <Input value={propertyName} disabled variant="monospaced" />
            )}
          </div>
          <div className="w-1/3 flex-1 shrink-0">
            <Input
              error={
                !!formState.errors.properties?.[propertyName]?._display_name
              }
              {...register(`properties.${propertyName}._display_name`, {
                required: "This field is required",
              })}
            />
            {formState.errors.properties?.[propertyName]?._display_name && (
              <ErrorHint>
                {
                  formState.errors.properties?.[propertyName]?._display_name
                    ?.message
                }
              </ErrorHint>
            )}
          </div>
          <div className="flex w-1/3 flex-1 items-center justify-stretch gap-x-2">
            {isNew ? (
              <Controller<EntitySchemaResource, `properties.${string}._type`>
                name={`properties.${propertyName}._type`}
                render={({ field }) => (
                  <Select
                    dataLoc={`entity-schema-new-field-type-${propertyName}`}
                    options={NEW_PROPERTY_TYPE_SELECT_OPTIONS}
                    placeholder="Select type"
                    value={field.value}
                    fullWidth
                    onChange={(value) => {
                      const newType = value as PropertyDefinitionOutputTypeEnum;
                      field.onChange(newType);
                      handleNewTypeChange(newType);
                    }}
                  />
                )}
              />
            ) : (
              <Controller<EntitySchemaResource, `properties.${string}._type`>
                name={`properties.${propertyName}._type`}
                render={({ field }) => {
                  const pluralDisplayName = getValues("_display_name_plural");
                  const singularDisplayName = getValues(
                    "_display_name_singular",
                  );
                  return (
                    <PropertyTypeSelector
                      pluralDisplayName={pluralDisplayName}
                      property={{
                        type: property._type,
                        display_name: property._display_name,
                        enum_schema: undefined,
                        relation_schema: undefined,
                      }}
                      relSchema={property._rel_schema}
                      relationCardinality={property._cardinality}
                      schemaId={schemaId}
                      singularDisplayName={singularDisplayName}
                      value={field.value}
                    />
                  );
                }}
              />
            )}
            {isNew ? (
              <div className="flex w-5 items-center justify-end">
                <IconButton
                  dataLoc={`entity-schema-remove-field-${propertyName}`}
                  icon={faTrashAlt}
                  size="xs"
                  onClick={() => onRemove(propertyName)}
                />
              </div>
            ) : (
              isValidSensitiveType &&
              propertyName !== ENTITY_ID_FIELD_NAME &&
              showDataObfuscation && (
                <div className="flex w-5 items-center justify-end">
                  <AdvancedOptions
                    propertyPath={`properties.${propertyName}`}
                  />
                </div>
              )
            )}
          </div>
        </div>
        {isPredefinedValuesProp && (
          <PredefinedValuesEditor
            allValuesEditable={isNew}
            canManageValues={canManageValues}
            propertyName={propertyName}
          />
        )}
      </div>
      {isValidSensitiveType && property._sensitive && showDataObfuscation && (
        <div className="ml-7">
          <MaskingOptions propertyPath={`properties.${propertyName}`} />
        </div>
      )}
    </>
  );
};

type PropertiesSchemaEditorProps = {
  isLoading?: boolean;
};

export const SchemaPropertiesEditor: React.FC<PropertiesSchemaEditorProps> = ({
  isLoading,
}) => {
  const form = useFormContext<EntitySchemaResource>();
  const isFwdOrAdmin = useIsFWDOrAdmin();

  const schemaId = form.getValues("_id");
  const properties = useWatch({
    control: form.control,
    name: "properties",
  });
  const orderedFields = getOrderedPropertiesByKey(properties);

  const handleAddField = () => {
    const newKey = createNewPropertyKey();
    form.setValue(
      `properties.${newKey}`,
      getNewEntityProperty(form.getValues("properties")),
      { shouldDirty: true },
    );
  };

  const handleRemoveField = (propertyName: string) => {
    const currentProperties = form.getValues("properties");
    const { [propertyName]: _removed, ...remaining } = currentProperties;
    form.setValue("properties", remaining, { shouldDirty: true });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active.id || !over?.id) return;

    // Get the current order of items
    const items = active.data.current?.sortable.items as string[];
    const oldIndex = items.indexOf(active.id as string);
    const newIndex = items.indexOf(over.id as string);

    // If the item was dropped in the same position, do nothing
    if (oldIndex === newIndex) return;

    // Create a new array with the reordered items
    const reorderedItems = [...items];
    const [movedItem] = reorderedItems.splice(oldIndex, 1);
    reorderedItems.splice(newIndex, 0, movedItem);

    // Get all properties that need to be updated with their new order
    const propertiesToUpdate = reorderedItems.map((id, index) => ({
      id,
      newOrder: index + 1, // Assuming order starts from 1
    }));

    // Update all affected properties' orders
    propertiesToUpdate.forEach(({ id, newOrder }) => {
      form.setValue(`properties.${id}._order`, newOrder, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  };

  return (
    <div className="relative flex flex-col gap-y-3">
      <div className="ml-7 flex gap-x-2 font-inter-normal-12px text-gray-500">
        <div className="w-1/3 flex-1">Name</div>
        <div className="w-1/3 flex-1">Display Name</div>
        <div className="w-1/3 flex-1">Type</div>
      </div>
      {isLoading ? (
        times(7, (i) => <PropertyFields key={i} isLoading />)
      ) : (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedFields}
            strategy={verticalListSortingStrategy}
          >
            {orderedFields.map((propertyName) => (
              <PropertyFields
                key={propertyName}
                canManageValues={isFwdOrAdmin}
                canReorder={isFwdOrAdmin}
                property={form.getValues(`properties.${propertyName}`)}
                propertyName={propertyName}
                schemaId={schemaId}
                onRemove={handleRemoveField}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
      {!isLoading && isFwdOrAdmin && (
        <div className="mt-1 ml-7">
          <Button
            dataLoc="entity-schema-add-field"
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={handleAddField}
          >
            Add field
          </Button>
        </div>
      )}
    </div>
  );
};
